import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
} from 'recharts';
import { reportsApi } from '../../../api/client';
import RangeBar from './components/RangeBar';
import ReportTable from './components/ReportTable';
import useRangeState from './components/useRangeState';
import ExportButtons from './components/ExportButtons';
import HelpButton from '../../help/HelpButton';

/**
 * AUX / Not-Ready Reasons (2026-08-10) — the dedicated AUX-category report.
 * Pivots agent Not-Ready time BY reason code (Lunch/Break/Meeting/...), with
 * total time, occurrences, avg duration, and a per-agent drill-down. Uses the
 * per-tenant AUX codes. WFM shrinkage input. Source: agent_state_history.
 */
function fmtSecs(s) {
  s = Number(s) || 0;
  const h = Math.floor(s / 3600), m = Math.floor((s % 3600) / 60), r = s % 60;
  return h > 0 ? `${h}h ${m}m` : (m > 0 ? `${m}m ${r}s` : `${r}s`);
}

export default function AuxReasons() {
  const [range, setRange, apiParams] = useRangeState('this_week');
  const [openReason, setOpenReason] = useState(null);
  const { data, isLoading, isError, error, refetch, isFetching } = useQuery({
    queryKey: ['reports', 'aux-reasons', apiParams],
    queryFn: () => reportsApi.auxReasons(apiParams),
    keepPreviousData: true,
  });

  const rows = data?.rows || [];
  const chart = rows.map((r) => ({ reason: r.reason, minutes: Math.round(r.total_sec / 60) }));

  return (
    <div>
      <RangeBar value={range} onChange={setRange} />
      <div className="flex items-center justify-between mb-2">
        <h2 className="text-base font-semibold">AUX / Not-Ready reasons</h2>
        <div className="flex items-center gap-2">
          <span className="text-xs text-surface-muted">{data?.subtitle}</span>
          <HelpButton moduleKey="reports" />
          <ExportButtons report="aux-reasons" params={apiParams} disabled={isLoading} />
          <button onClick={() => refetch()}
            className="px-2 py-1 text-xs rounded-thin border border-surface-border dark:border-surface-dark-border hover:bg-surface-subtle dark:hover:bg-surface-dark-subtle">
            ↻ {isFetching ? '…' : 'Refresh'}
          </button>
        </div>
      </div>
      <p className="text-xs text-surface-muted mb-3">
        Team time by Not-Ready reason. Click a reason to see the per-agent breakdown. Source: agent state history.
      </p>

      {isError && (
        <div className="text-sm text-red-600 p-3 rounded-thin border border-red-300/40 mb-3">
          {error?.detail || error?.message || 'Failed to load report.'}
        </div>
      )}

      {chart.length > 0 && (
        <div className="mb-4 rounded-thin border border-surface-border dark:border-surface-dark-border p-3 bg-surface-panel dark:bg-surface-dark-panel">
          <h3 className="text-sm font-semibold mb-2">Time by reason (minutes)</h3>
          <div style={{ height: 220 }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={chart} margin={{ top: 8, right: 20, bottom: 24, left: 0 }}>
                <CartesianGrid stroke="rgba(120,120,120,0.15)" strokeDasharray="3 3" />
                <XAxis dataKey="reason" tick={{ fontSize: 11 }} angle={-20} dy={10} height={44} />
                <YAxis tick={{ fontSize: 11 }} />
                <Tooltip />
                <Bar dataKey="minutes" fill="#F5A623" name="Minutes" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      <table className="w-full text-sm">
        <thead className="text-left text-xs uppercase tracking-wider text-surface-muted">
          <tr>
            <th className="px-2 py-1">Reason</th>
            <th className="px-2 py-1 text-right">Total time</th>
            <th className="px-2 py-1 text-right">Occurrences</th>
            <th className="px-2 py-1 text-right">Avg duration</th>
            <th className="px-2 py-1"></th>
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 && (
            <tr><td colSpan={5} className="text-center text-xs text-surface-muted py-4">No AUX time in range.</td></tr>
          )}
          {rows.map((r) => (
            <React.Fragment key={r.reason}>
              <tr className="border-t border-surface-border dark:border-surface-dark-border">
                <td className="px-2 py-1.5 font-medium">{r.reason}</td>
                <td className="px-2 py-1.5 text-right font-mono">{fmtSecs(r.total_sec)}</td>
                <td className="px-2 py-1.5 text-right font-mono">{r.occurrences}</td>
                <td className="px-2 py-1.5 text-right font-mono">{fmtSecs(r.avg_sec)}</td>
                <td className="px-2 py-1.5 text-right">
                  <button className="text-continuum-600 hover:underline text-xs"
                    onClick={() => setOpenReason(openReason === r.reason ? null : r.reason)}>
                    {openReason === r.reason ? 'Hide' : 'By agent'}
                  </button>
                </td>
              </tr>
              {openReason === r.reason && (
                <tr className="bg-surface-subtle dark:bg-surface-dark-subtle">
                  <td colSpan={5} className="px-4 py-2">
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-x-6 gap-y-1 text-xs">
                      {Object.entries(r.agents || {}).sort((a, b) => b[1] - a[1]).map(([ext, sec]) => (
                        <div key={ext} className="flex justify-between">
                          <span className="text-surface-muted">{ext}</span>
                          <span className="font-mono">{fmtSecs(sec)}</span>
                        </div>
                      ))}
                    </div>
                  </td>
                </tr>
              )}
            </React.Fragment>
          ))}
        </tbody>
      </table>
    </div>
  );
}
